import { useEffect, useRef, useState } from 'react';
import { ArrowUp, Loader2, Mic, Paperclip, Square } from 'lucide-react';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { PendingAttachmentTray } from './AttachmentChip';
import { VoiceRecordingBar } from './VoiceRecordingBar';
import type { PendingAttachment } from '../hooks/useAttachmentUploads';
import { cn } from '@/lib/utils';
import { useAdvisorCopy } from '@/shared/hooks/useAdvisorCopy';

// Human-readable error copy for the voice subsystem. Falls back to a
// neutral message when the error is generic so the user isn't left
// staring at an empty composer.
const VOICE_ERROR_COPY: Record<string, string> = {
  'permission-denied':
    'Microphone access was blocked. Allow it in your browser settings to dictate.',
  'no-mic': 'No microphone detected.',
  'no-speech': '',
  aborted: '',
  generic: 'Voice input is unavailable right now.',
};

/**
 * Attachment wiring — either all of it, or an explicit refusal.
 *
 * It used to be three optional props, and one of them (`onAttach`) quietly
 * governed three separate affordances: the paperclip, the drop zone and
 * paste-to-attach. A surface that did not pass it lost all three with no
 * error, no warning, and nothing in a diff to notice. That is exactly what
 * happened to the side panel, and it shipped that way. Not passing it was also
 * worse than "no attaching": with no drop handler the browser default takes
 * over, so dropping a PDF navigated the tab away from the app.
 *
 * So silence is no longer a valid answer. A surface either wires attachments
 * up, or says `canAttach={false}` and explains itself. The dashboard hero is
 * the real case for the second: it is a launcher, not a chat — it hands your
 * text to /soteria and has no session for an upload to belong to.
 *
 * A union rather than three required-but-nullable props, because this also
 * rules out the half-wired states (files with no handler, a handler with no
 * tray) that a reviewer would have to notice by eye.
 */
export type ComposerAttachWiring =
  | {
      canAttach?: undefined;
      attachments: Array<PendingAttachment>;
      /** Called with chosen files — from the paperclip, a drop, or a paste. */
      onAttach: (files: Array<File>) => void;
      onRemoveAttachment: (key: string) => void;
    }
  | {
      /** No attach affordances here, deliberately. Say why at the call site. */
      canAttach: false;
      attachments?: never;
      onAttach?: never;
      onRemoveAttachment?: never;
    };

interface SoteriaComposerBaseProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  /** "Ask anything" on the landing hero, "Ask Soteria AI to analyze data..." in chat */
  placeholder?: string;
  /** Optional max width on the card; landing uses ~608px, chat uses 768px */
  maxWidthClass?: string;
  /** When set with 2+ entries, the placeholder typewriter-cycles through them
   *  until the user focuses or starts typing. Used on the landing hero to
   *  hint at sample prompts. */
  rotatingPlaceholders?: string[];
  /** Streaming-state controls. When `isStreaming` is true the send button
   *  flips into a stop button that calls `onStop`. Lets the user abort a
   *  slow Vertex AI response without reloading the page. */
  isStreaming?: boolean;
  /** Pending reply on surfaces without a server cancellation endpoint. */
  isPending?: boolean;
  onStop?: () => void;
}

type SoteriaComposerProps = SoteriaComposerBaseProps & ComposerAttachWiring;

// Typewriter cycle: types each prompt, pauses, deletes, moves to the next.
// Returns the current displayed string. Pauses the cycle when `paused`
// is true (focus / non-empty value) and resets to the first prompt.
function useTypewriterPlaceholder(prompts: string[], paused: boolean): string {
  const [text, setText] = useState('');
  const stateRef = useRef({ idx: 0, charIdx: 0, deleting: false });

  useEffect(() => {
    if (paused || prompts.length < 2) {
      setText('');
      stateRef.current = { idx: 0, charIdx: 0, deleting: false };
      return;
    }
    let timeout: number;
    const tick = () => {
      const s = stateRef.current;
      const target = prompts[s.idx % prompts.length];
      if (!s.deleting) {
        if (s.charIdx < target.length) {
          s.charIdx += 1;
          setText(target.slice(0, s.charIdx));
          timeout = window.setTimeout(tick, 35 + Math.random() * 25);
        } else {
          // hold the full prompt for a beat, then delete
          timeout = window.setTimeout(() => {
            s.deleting = true;
            tick();
          }, 1800);
        }
      } else {
        if (s.charIdx > 0) {
          s.charIdx -= 1;
          setText(target.slice(0, s.charIdx));
          timeout = window.setTimeout(tick, 18);
        } else {
          s.deleting = false;
          s.idx = (s.idx + 1) % prompts.length;
          timeout = window.setTimeout(tick, 250);
        }
      }
    };
    tick();
    return () => window.clearTimeout(timeout);
  }, [prompts, paused]);

  return text;
}

// Composer — verbatim from figma LandingPageDefault/SoteriaAIPage (via
// Figma MCP, fileKey YceV9LIrJunFHEXN6XRtjd). The same composer renders
// inside the landing hero AND pinned to the bottom of the chat area, so
// it lives in its own component and accepts placeholder/maxWidth as
// props.
export function SoteriaComposer(props: SoteriaComposerProps) {
  const copy = useAdvisorCopy();
  const {
    value,
    onChange,
    onSubmit,
    placeholder = copy('Ask anything'),
    maxWidthClass = 'max-w-[608px]',
    rotatingPlaceholders,
    isStreaming = false,
    isPending = false,
    onStop,
  } = props;
  // Narrowed ONCE, here, rather than testing `onAttach` at each of the four
  // places that need it. `attach` is null only when a surface has explicitly
  // opted out, and non-null means all three pieces are present — the type
  // makes a half-wired composer unrepresentable.
  const attach =
    props.canAttach === false
      ? null
      : {
          files: props.attachments,
          add: props.onAttach,
          remove: props.onRemoveAttachment,
        };
  const [isFocused, setIsFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const animated = useTypewriterPlaceholder(
    rotatingPlaceholders ?? [],
    isFocused || value.length > 0,
  );
  const activePlaceholder =
    animated && !isFocused && value.length === 0 ? animated : placeholder;
  // The voice hook commits FINALISED transcript chunks back through the
  // callback. We append to whatever the user has typed so dictation and
  // keyboard input compose naturally (you can dictate, edit by hand,
  // then dictate more).
  const voice = useVoiceInput((finalText) =>
    onChange(value ? `${value.replace(/\s+$/, '')} ${finalText}` : finalText),
  );
  const voiceErrorCopy = voice.error ? VOICE_ERROR_COPY[voice.error] : '';

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height =
      Math.min(textareaRef.current.scrollHeight, 200) + 'px';
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      // Not while an answer is streaming: the send button is a Stop button
      // then, and Enter used to send anyway — abandoning the half-written
      // answer on screen. The text stays in the box for after.
      if (isStreaming) return;
      // An attachment with no text is still a message worth sending.
      if (!isPending && (value.trim() || (attach?.files.length ?? 0) > 0))
        onSubmit();
    }
  };

  // A file on its own is a message ("here, look at this"), so an attachment
  // enables send even with no text.
  const canSend =
    !isPending && (value.trim().length > 0 || (attach?.files.length ?? 0) > 0);

  const pickFiles = (list: FileList | null) => {
    if (!list?.length || !attach) return;
    attach.add(Array.from(list));
  };

  return (
    <div className={cn('w-full', maxWidthClass)}>
      <div
        className={cn(
          'bg-card relative rounded-3xl border shadow-sm focus-within:ring-1 focus-within:ring-ring/40 focus-within:border-primary/50 transition-all overflow-visible',
          // A dashed purple edge while a file is over the composer — the drop
          // target has to be visible or drag-and-drop is a guess.
          dragging
            ? 'border-primary border-dashed bg-primary/5'
            : 'border-border',
        )}
        onDragOver={
          attach
            ? (e) => {
                // Only for a real file drag — dragging selected text across
                // the composer must not arm the drop zone.
                if (!e.dataTransfer.types.includes('Files')) return;
                e.preventDefault();
                setDragging(true);
              }
            : undefined
        }
        onDragLeave={attach ? () => setDragging(false) : undefined}
        onDrop={
          attach
            ? (e) => {
                if (!e.dataTransfer.types.includes('Files')) return;
                // Without this the browser navigates the tab to the file.
                e.preventDefault();
                setDragging(false);
                pickFiles(e.dataTransfer.files);
              }
            : undefined
        }
      >
        <div className="flex flex-col items-start px-5 py-3 gap-2">
          {/* Staged files, above the text they will be sent with. */}
          {attach && attach.files.length ? (
            <PendingAttachmentTray
              attachments={attach.files}
              onRemove={attach.remove}
            />
          ) : null}
          {/* Live recording panel. Slides above the textarea while voice
              is active; the textarea continues to accept keyboard input
              underneath, so the user can edit committed text mid-session. */}
          {voice.isListening && (
            <VoiceRecordingBar
              isSpeaking={voice.isSpeaking}
              interim={voice.interim}
              onStop={voice.stop}
            />
          )}

          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={
              attach
                ? (e) => {
                    // A screenshot on the clipboard is the fastest way an
                    // advisor shares what they are looking at. Only intercept
                    // when the clipboard actually carries files — otherwise
                    // normal text pasting must be left alone.
                    const files = Array.from(e.clipboardData.files);
                    if (!files.length) return;
                    e.preventDefault();
                    attach.add(files);
                  }
                : undefined
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={activePlaceholder}
            rows={1}
            className="w-full bg-transparent outline-none resize-none text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground py-2 min-h-[44px]"
          />

          <div className="mt-1 flex h-7 w-full items-center justify-between gap-2">
            {/* Left cluster: attach, then any voice-error copy. Attach sits on
                the left because it acts on the message you are composing, while
                the right-hand pair (dictate, send) acts on sending it. */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {attach && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      pickFiles(e.target.files);
                      // Reset so choosing the SAME file twice still fires
                      // onChange (the browser suppresses it otherwise).
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="p-2 rounded-lg transition-colors hover:bg-muted text-muted-foreground"
                    aria-label={copy('Attach a file')}
                    title={copy('Attach an image or PDF')}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                </>
              )}
              <p className="truncate text-xs text-destructive">
                {voiceErrorCopy}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Voice — Web Speech API. Renders only when the browser
                  exposes the API (Chrome/Edge/Safari yes, Firefox no).
                  Idle mic icon hides itself once the VoiceRecordingBar
                  takes over above — that has its own stop control. */}
              {voice.supported && !voice.isListening && (
                <button
                  type="button"
                  className="p-2 rounded-lg transition-colors hover:bg-muted text-muted-foreground"
                  aria-label={copy('Start dictation')}
                  title={copy('Dictate')}
                  onClick={voice.toggle}
                >
                  <Mic className="w-4 h-4" />
                </button>
              )}

              {isStreaming && onStop ? (
                <button
                  type="button"
                  onClick={onStop}
                  className="flex items-center justify-center h-8 w-8 rounded-full bg-foreground hover:bg-foreground/90 cursor-pointer transition-colors"
                  aria-label={copy('Stop generating')}
                  title={copy('Stop generating')}
                >
                  <Square className="h-3.5 w-3.5 text-background fill-background" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => canSend && onSubmit()}
                  disabled={!canSend}
                  // Neutral, not brand-filled. A saturated violet circle was
                  // the loudest element on an otherwise quiet screen; send is a
                  // utility control, so it goes solid only once there is
                  // something to send, and in the foreground colour the app
                  // already uses for primary actions.
                  className={cn(
                    'flex items-center justify-center h-8 w-8 rounded-full transition-colors',
                    canSend
                      ? 'bg-foreground text-background hover:bg-foreground/90 cursor-pointer'
                      : 'bg-muted text-muted-foreground cursor-not-allowed',
                  )}
                  aria-label={copy('Send message')}
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" />
                  ) : (
                    <ArrowUp className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
